import { COUNTRIES } from "./countries";
import { formatDay, formatEta, toNum } from "./format";
import { MODE_LABEL } from "./modes";
import type { IParcel, IParcelEvent } from "./proto";

export const JOURNEY_STEPS = ["Added", "On its way", "In Kenya", "Out for delivery", "Delivered"];

const STEP_OF: Record<string, number> = {
  REGISTERED: 0,
  RECEIVED_ABROAD: 1,
  RECEIVED_KENYA: 2,
  ON_HOLD: 2,
  OUT_FOR_DELIVERY: 3,
  DELIVERED: 4,
};

export function journeyStep(status?: string | null): number {
  return STEP_OF[status ?? ""] ?? 0;
}

export function parcelTitle(parcel: IParcel): string {
  return parcel.description || parcel.trackingNumber || "Parcel";
}

export function countryName(code?: string | null): string {
  return COUNTRIES.find((c) => c.code === code)?.name ?? code ?? "";
}

export function routeLabel(parcel: IParcel): string {
  const from = countryName(parcel.originCountry);
  const by = parcel.mode ? MODE_LABEL[parcel.mode]?.toLowerCase() : "";
  return [from && `From ${from}`, by && `by ${by}`].filter(Boolean).join(" ");
}

export function isPayable(parcel: IParcel): boolean {
  return parcel.status === "RECEIVED_KENYA" && toNum(parcel.amountChargedKes) > 0;
}

export function needsCustomer(parcel: IParcel): boolean {
  return isPayable(parcel) || parcel.status === "ON_HOLD";
}

export function deliveryLabel(parcel: IParcel): string {
  if (parcel.deliveryMethod === "locker") return parcel.deliveryArea ? `${parcel.deliveryArea} locker` : "ParcelPoint locker";
  if (parcel.deliveryMethod === "home") {
    const address = (parcel.deliveryAddress ?? "").replace(/[,\s]+$/, "");
    return address ? `Home, ${address}` : "Home delivery";
  }
  if (parcel.deliveryMethod === "custom") return parcel.deliveryAddress || "Your chosen address";
  return "Not chosen yet";
}

export function statusHeadline(parcel: IParcel): string {
  switch (parcel.status) {
    case "REGISTERED":
      return `Waiting for it at our ${countryName(parcel.originCountry)} warehouse`;
    case "RECEIVED_ABROAD":
      return "On its way to Kenya";
    case "RECEIVED_KENYA":
      return "In Kenya, ready to pay";
    case "ON_HOLD":
      return "On hold";
    case "OUT_FOR_DELIVERY":
      return "Out for delivery";
    case "DELIVERED":
      return "Delivered";
    default:
      return "";
  }
}

export function statusDetail(parcel: IParcel): string {
  switch (parcel.status) {
    case "REGISTERED":
      return parcel.expectedArrival
        ? `The seller expects to deliver it there on ${formatDay(parcel.expectedArrival)}. We take over from that point.`
        : "We take over as soon as the seller delivers it there.";
    case "RECEIVED_ABROAD":
      return "We have it and it is travelling to Kenya. We will ask you to pay once it lands.";
    case "RECEIVED_KENYA":
      return "Pay to release it for delivery.";
    case "ON_HOLD":
      return "We need something from you before it can move. See the message from our team below.";
    case "OUT_FOR_DELIVERY":
      return `Paid. We are bringing it to ${deliveryDestination(parcel)}.`;
    case "DELIVERED":
      return "Thank you for shipping with ParcelPoint.";
    default:
      return "";
  }
}

function deliveryDestination(parcel: IParcel): string {
  if (parcel.deliveryMethod === "locker") return parcel.deliveryArea ? `the ${parcel.deliveryArea} locker` : "your locker";
  if (parcel.deliveryMethod === "home") return "your home";
  return parcel.deliveryAddress || "you";
}

export function upcomingEta(parcel: IParcel): string {
  if (parcel.status === "DELIVERED" || parcel.status === "ON_HOLD" || !parcel.etaMaxAt) return "";
  if (new Date(parcel.etaMaxAt).getTime() < Date.now()) return "";
  return formatEta(parcel.etaMinAt, parcel.etaMaxAt);
}

export function timelineLabel(event: IParcelEvent, parcel: IParcel): string {
  switch (event.toStatus) {
    case "REGISTERED":
      return "You added this parcel";
    case "RECEIVED_ABROAD":
      return `Received at our ${countryName(parcel.originCountry)} warehouse`;
    case "RECEIVED_KENYA":
      return event.fromStatus === "ON_HOLD" ? "Hold released" : "Arrived in Kenya";
    case "ON_HOLD":
      return "Put on hold";
    case "OUT_FOR_DELIVERY":
      return "Paid, out for delivery";
    case "DELIVERED":
      return "Delivered";
    default:
      return "";
  }
}
